import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, BookOpen } from 'lucide-react';
import { useLab } from '../LabContext';
import { LabLayout, Card, NimnyArt } from '../LabUI';

// Generic landing for any child user that doesn't map to a legacy
// /lab/max or /lab/anita route. New children created via the parent
// admin tool land here. Their actual curriculum is wired up in the
// next patch (course_template -> dynamic lesson route).
const ChildPlaceholder = () => {
  const { user, language } = useLab();
  if (!user || user.role !== 'child') {
    // Hard guard: never render to a non-child session.
    return (
      <LabLayout>
        <Card className="mt-4 p-5">
          <div className="text-amber-200/70 text-sm font-sans">
            {language === 'ru' ? 'Доступ только для ребёнка.' : 'Child only.'}
          </div>
        </Card>
      </LabLayout>
    );
  }

  const courseTemplate = user.courseTemplate || 'max';
  const courseLabel =
    courseTemplate === 'max'
      ? language === 'ru'
        ? 'Курс «Макса»'
        : "Max's course"
      : courseTemplate;

  return (
    <LabLayout>
      <section className="mt-2 flex flex-col items-center text-center">
        <NimnyArt size="md" alt={user.name} />
        <h1
          className="mt-3 text-3xl text-[#f3d9a4]"
          style={{ fontFamily: 'var(--font-heading)' }}
          data-testid="child-placeholder-title"
        >
          {language === 'ru' ? `Привет, ${user.name}!` : `Hello, ${user.name}!`}
        </h1>
        <p className="mt-2 text-amber-200/70 text-sm font-serif max-w-sm">
          {language === 'ru'
            ? 'Твой аккаунт создан. Курс готовится — родитель скоро откроет первые занятия.'
            : 'Your account is ready. Your course is being prepared — your parent will open the first lessons soon.'}
        </p>
      </section>

      <Card className="mt-6 p-4" data-testid="child-placeholder-course-card">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-full bg-amber-300/8 border border-amber-300/15 flex items-center justify-center text-amber-200/85">
            <BookOpen className="w-[18px] h-[18px]" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[#f5ebe0]/95 font-sans text-sm">
              {language === 'ru' ? 'Назначенный курс' : 'Assigned course'}
            </div>
            <div className="text-amber-200/65 text-xs font-mono mt-0.5">{courseLabel}</div>
          </div>
        </div>
      </Card>

      <div className="mt-6 text-center">
        <Link
          to="/lab"
          className="inline-flex items-center gap-2 text-amber-200/65 text-sm font-sans hover:text-amber-200"
          data-testid="child-placeholder-back"
        >
          {language === 'ru' ? 'На главную' : 'Home'}
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
    </LabLayout>
  );
};

export default ChildPlaceholder;
